/**
 * `entityToPrimitives` — the per-entity dispatcher for the vector PDF exporter
 * (PDF-1). It owns the `Entity → CadPrimitive[]` switch that fans a TYPED
 * drawing entity out to its per-entity converter.
 *
 * Kept SEPARATE from {@link ./cadPrimitive} (the IR type module) so the IR type
 * module never has to import the converters: each converter imports the IR
 * TYPES one-way from `./cadPrimitive`, and this dispatcher imports both the IR
 * types (from `./cadPrimitive`) and the converters (from `./converters/*`). That
 * one-way layering breaks the `cadPrimitive → converters → cadPrimitive` import
 * cycle (dependency-cruiser `no-circular`).
 */

import {
  tableAsBlockReference,
  type DrawingEntity,
} from "@dxf-ai-editor/drawing-model";

import type { CadConvertContext, CadPrimitive } from "./cadPrimitive";

import { lineToPrimitives } from "./converters/lineConverter";
import { polylineToPrimitives } from "./converters/polylineConverter";
import {
  circleToPrimitives,
  arcToPrimitives,
  ellipseToPrimitives,
} from "./converters/curveConverter";
import { insertToPrimitives } from "./converters/insertConverter";
import { textToPrimitives } from "./converters/textConverter";
import { hatchToPrimitives } from "./converters/hatchConverter";
import { dimensionToPdfPrimitives } from "./converters/dimensionConverter";
import { multileaderToPdfPrimitives } from "./converters/multileaderConverter";
import { splineToPdfPrimitives } from "./converters/splineConverter";
import { wipeoutToPdfPrimitives } from "./converters/wipeoutConverter";
import { heavyPolylineToPrimitives } from "./converters/heavyPolylineConverter";
import { oleFrameToPdfPrimitives } from "./converters/oleFrameConverter";

/**
 * Convert a single TYPED drawing entity to its CadPrimitive list. Returns an
 * empty array for entity types not covered yet.
 *
 * PDF-1 = geometry (LINE/PLINE/CIRCLE/ARC/ELLIPSE/INSERT + ATTRIB text-runs).
 * PDF-2 = single-line TEXT (DTEXT) → text-run (rendered by the backend).
 *
 * DEFERRED:
 *   - standalone MTEXT multi-run rich formatting → PDF-2b (inline codes,
 *     stacks, paragraph wrap). NOT routed here.
 *   - HATCH = PDF-5a; DIMENSION / MULTILEADER = PDF-5b; SPLINE = PDF-5c (below).
 */
export function entityToPrimitives(
  entity: DrawingEntity,
  ctx: CadConvertContext,
): CadPrimitive[] {
  switch (entity.type) {
    case "Line":
      return lineToPrimitives(entity, ctx);
    case "LWPOLYLINE":
      return polylineToPrimitives(entity, ctx);
    case "Circle":
      return circleToPrimitives(entity, ctx);
    case "Arc":
      return arcToPrimitives(entity, ctx);
    case "Ellipse":
      return ellipseToPrimitives(entity, ctx);
    case "Block":
      return insertToPrimitives(entity, ctx);
    case "Text":
      return textToPrimitives(entity, ctx);
    case "Hatch":
      return hatchToPrimitives(entity, ctx);
    case "Dimension":
      return dimensionToPdfPrimitives(entity, ctx);
    case "Multileader":
      return multileaderToPdfPrimitives(entity, ctx);
    case "Spline":
      return splineToPdfPrimitives(entity, ctx);
    case "Wipeout":
      return wipeoutToPdfPrimitives(entity, ctx);
    case "Polyline":
      // The legacy HEAVY polyline (2D / 3D / polygon mesh / poly-face mesh) —
      // a different AutoCAD object from LWPOLYLINE, with its own subtype-gated
      // geometry (@codex PR #785 round 2, F1).
      return heavyPolylineToPrimitives(entity, ctx);
    case "Table":
      // ACAD_TABLE literally IS a block reference to its anonymous `*T<N>`
      // graphics block (its first subclass is `AcDbBlockReference`), so the
      // plot resolves the ONE shared `tableAsBlockReference` view — the same
      // projection `cad-core`'s `select.ts` / `bounds.ts` resolve — and sends
      // it down the INSERT path, which already knows how to walk a block
      // definition. The grid LINEs and the cell MTEXT plot out of that block;
      // NOT plotted is anything the `*T<N>` block does not contain (cell
      // styles / merges / formulas are content model, not graphics — see
      // `tableEntitySchema`'s boundary doc).
      return insertToPrimitives(tableAsBlockReference(entity), ctx);
    case "OLE Frame":
      // The frame rectangle only — the embedded EMF raster is deliberately
      // not rendered anywhere in this project (see the converter's doc).
      return oleFrameToPdfPrimitives(entity, ctx);
    default:
      // Not yet in scope. MTEXT (PDF-2b) is deferred (documented in
      // PLAN-plot-pdf.md §5) — emit nothing rather than a simplified
      // placeholder. HATCH is PDF-5a; DIMENSION / MULTILEADER are PDF-5b;
      // SPLINE is PDF-5c; WIPEOUT mask + OCS/extrusion are PDF-5d (above).
      //
      // SOLID / TRACE / 3DFACE: there is NO `Solid` / `Trace` / `3DFace`
      // entity type in the drawing-model schema, nor in the DXF entityParser —
      // the importer never produces them, so there is nothing to plot here.
      // Filled-polygon (SOLID/TRACE) and quad-face (3DFACE) plotting is BLOCKED
      // on the importer/model adding those entity types first (PDF-5d-followup
      // / importer scope), NOT a plot-side gap. We deliberately do NOT fabricate
      // a Solid entity path (CLAUDE.md PRIORITY #1 — no fakes).
      return [];
  }
}
